import React, { useEffect, useRef } from 'react';
import { Table3, Table3Ref } from '@economic/taco';
import { StandardAccount } from '~/types/StandardAccount';
import { t } from '@lingui/macro';
import { ViewProps } from './types';
import { useStandardAccounts } from '../shared/hooks/masterdata/useStandardAccounts';

interface Props extends ViewProps<StandardAccount> {
    selectedAccountNumber?: number;
    useCache?: boolean;
}

export enum StandardAccountType {
    Heading = 0,
    ProfitAndLoss = 1,
    BalanceSheet = 2,
    Sum = 3,
}

export interface StandardAccountModel {
    name: string;
    id: number;
    type: StandardAccountType;
}

const isHeading = (accountTypeNumber: StandardAccountType) => accountTypeNumber === StandardAccountType.Heading;

const isAvailable = (accountTypeNumber: StandardAccountType) =>
    accountTypeNumber === StandardAccountType.Heading ||
    accountTypeNumber === StandardAccountType.ProfitAndLoss ||
    accountTypeNumber === StandardAccountType.BalanceSheet;

const findActiveIndex = (accounts: StandardAccountModel[], selectedStandardAccountId?: number) => {
    const activeIndex = accounts?.findIndex(account => account.id === selectedStandardAccountId);
    return activeIndex >= 0 ? activeIndex : undefined;
};

export const StandardAccounts = ({ onSelected: handleSelected, selectedAccountNumber, useCache }: Props) => {
    const standardAccountsQuery = useStandardAccounts({
        options: { useCache },
    });

    const accounts = standardAccountsQuery.data.filter(account => isAvailable(account.type));

    const typeToLabelDict = {
        [StandardAccountType.Heading]: t`Heading`,
        [StandardAccountType.ProfitAndLoss]: t`Profit and loss`,
        [StandardAccountType.BalanceSheet]: t`Balance sheet`,
    };

    const tableRef = useRef<Table3Ref>(null);
    useEffect(() => {
        // This is used to prevent the dialog to automatically focus on the first
        // clickable element (in this case, the search bar)
        tableRef?.current?.focus();
    }, []);

    return (
        <div>
            <h2>{t`Standard Chart of Account`}</h2>

            <Table3<StandardAccountModel>
                id="select-standard-account-table"
                preset="simple"
                ref={tableRef}
                enableRowActive
                enableSearch
                data={accounts}
                onRowClick={row => {
                    if (!isHeading(row.type)) {
                        handleSelected(row);
                    }
                }}
                enableRowClick={row => !isHeading(row.type)}
                defaultRowActiveIndex={findActiveIndex(accounts, selectedAccountNumber)}>
                <Table3.Column<StandardAccountModel>
                    accessor="id"
                    header={t`No`}
                    defaultWidth={75}
                    sort="asc"
                    renderer={({ value, row }) => (isHeading(row.type) ? <b>{value}</b> : value)}
                />
                <Table3.Column<StandardAccountModel>
                    accessor="name"
                    align="left"
                    header={t`Name`}
                    renderer={({ value, row }) => (isHeading(row.type) ? <b>{value}</b> : value)}
                />
                <Table3.Column<StandardAccountModel>
                    accessor="type"
                    header={t`Type`}
                    renderer={({ value, row }) =>
                        isHeading(row.type) ? <b>{typeToLabelDict[value]}</b> : typeToLabelDict[value] ?? ''
                    }
                />
            </Table3>
        </div>
    );
};
